import React, { useState, useEffect } from "react";
import axios from "axios";
import "./Timer.css";

export default function Timer({
  userId,
  userType,
  asFloating = false
}: {
  userId?: string;
  userType?: string;
  asFloating?: boolean;
}) {
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [visible, setVisible] = useState(false);
  const [clearing, setClearing] = useState(false);

  const actualUserId = userId || localStorage.getItem("VisiteurId") || "";
  const actualUserType = userType || "visiteur";

  useEffect(() => {
    const handleCartExpire = async () => {
      setClearing(true);
      try {
        await axios.post(
          `${process.env.REACT_APP_PHP_APP_URL}/panier/delete/all/${actualUserId}`
        );
        if (actualUserType === "client") {
          localStorage.removeItem("productsClient");
        } else if (actualUserType === "visiteur") {
          localStorage.removeItem("productVisiteur");
        }
        localStorage.removeItem("cartExpiration");
        localStorage.removeItem("StartDate");
        window.dispatchEvent(new Event("panier-updated"));
      } catch (err) {
        console.error("Failed to clear cart:", err);
      } finally {
        setClearing(false);
      }
    };

    const updateTimer = () => {
      // Rely only on cartExpiration key — some pages (e.g. HomeVisiteur) don't
      // write the local cart array, so checking its length is unreliable.
      const expStr = localStorage.getItem("cartExpiration");
      if (!expStr) {
        setVisible(false);
        return;
      }

      const expTime = parseInt(expStr, 10);
      if (isNaN(expTime)) {
        setVisible(false);
        return;
      }

      const diffMs = expTime - Date.now();

      if (diffMs <= 0) {
        setVisible(false);
        if (!clearing) handleCartExpire();
      } else {
        setVisible(true);
        const totalSeconds = Math.floor(diffMs / 1000);
        setMinutes(Math.floor(totalSeconds / 60));
        setSeconds(totalSeconds % 60);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    window.addEventListener("panier-updated", updateTimer);

    return () => {
      clearInterval(interval);
      window.removeEventListener("panier-updated", updateTimer);
    };
  }, [actualUserId, actualUserType, clearing]);

  if (!visible) return null;

  const totalSecs = minutes * 60 + seconds;
  const isWarning  = totalSecs <= 600 && totalSecs > 120; // ≤ 10 min remaining
  const isCritical = totalSecs <= 120;                     // ≤ 2 min remaining

  const urgency = isCritical ? "critical" : isWarning ? "warning" : "normal";

  // Always render the premium floating popup (bottom-right)
  return (
    <div className={`ctm-popup ctm-popup--${urgency}`}>
      <div className="ctm-popup-inner">
        <div className="ctm-popup-icon">
          <i className="bi bi-clock-history"></i>
        </div>
        <div className="ctm-popup-body">
          <span className="ctm-popup-label">Panier expire dans</span>
          <span className="ctm-popup-time">
            {minutes.toString().padStart(2, "0")}
            <span className="ctm-popup-colon">:</span>
            {seconds.toString().padStart(2, "0")}
          </span>
        </div>
      </div>
      {/* progress bar draining left */}
      <div className="ctm-popup-bar-track">
        <div
          className="ctm-popup-bar-fill"
          style={{ width: `${Math.min(100, (totalSecs / 3000) * 100)}%` }}
        />
      </div>
    </div>
  );
}
